import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { FileText, Mic, Instagram, Shield, ArrowUpRight, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  {
    id: 'written-reports',
    icon: FileText,
    title: 'Hard-Hitting Written Reports',
    description:
      "Breaking news, deep dives, and exclusive investigations. The stories mainstream media sanitise, ignore, or get wrong.",
    href: '/stories',
    external: false,
  },
  {
    id: 'instagram',
    icon: Instagram,
    title: 'Daily Underworld Updates',
    description:
      "Fast, factual, zero filter coverage across Instagram. Australia's criminal landscape as it happens.",
    href: 'https://instagram.com/ryannaumenko',
    external: true,
  },
  {
    id: 'podcast',
    icon: Mic,
    title: 'The Outlaw Media Podcast',
    description:
      "No-holds-barred conversations with insiders, ex-criminals, and investigators. Raw, authentic, unfiltered.",
    href: 'https://youtube.com/@ryan.naumenko',
    external: true,
  },
  {
    id: 'insider-access',
    icon: Shield,
    title: 'Insider Access & Sources',
    description:
      "Real relationships built over years in Victoria's underworld. We don't just report - we know the players.",
    href: '/stories',
    external: false,
  },
];

const ServicesSection = () => {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/track/service-click/stats`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { counts: {} }))
      .then((d) => { if (!cancelled) setCounts(d.counts || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const trackClick = (id) => {
    // Fire-and-forget with keepalive so external nav doesn't drop it.
    try {
      fetch(`${API}/api/track/service-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_id: id }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* ignore */ }
    // Optimistically bump local count so re-ranking feels instant on re-render
    setCounts((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  };

  const ordered = [...SERVICES].sort(
    (a, b) => (counts[b.id] || 0) - (counts[a.id] || 0),
  );
  const topId = ordered[0]?.id;

  return (
    <section id="services" className="pt-6 pb-10 px-4 relative" data-testid="services-section">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/40 to-black"></div>

      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-black mb-1">
            WHAT WE <span className="text-red-500">DELIVER</span>
          </h2>
          <p className="text-xs text-gray-400">Ranked by reader interest - busiest sits top-left</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ordered.map(({ id, icon: Icon, title, description, href, external }) => {
            const count = counts[id] || 0;
            const isTop = id === topId && count > 0;
            const Tag = external ? 'a' : 'a';
            const targetProps = external
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {};
            return (
              <Tag
                key={id}
                href={href}
                {...targetProps}
                onClick={() => trackClick(id)}
                data-testid={`service-card-${id}`}
                className="block group focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded-lg"
              >
                <Card
                  className={`bg-gray-900/50 border backdrop-blur-sm transition-all duration-200 group-hover:bg-gray-900/70 ${
                    isTop
                      ? 'border-red-500/60 shadow-[0_0_18px_rgba(220,38,38,0.18)] group-hover:border-red-400'
                      : 'border-red-500/25 group-hover:border-red-500/60 group-hover:shadow-[0_0_18px_rgba(220,38,38,0.18)]'
                  }`}
                >
                  <CardContent className="p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-500/10 rounded-md shrink-0 transition-colors group-hover:bg-red-500/20">
                        <Icon className="h-4 w-4 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-bold mb-1 text-white leading-tight">{title}</h3>
                          {isTop ? (
                            <span
                              data-testid={`service-card-${id}-top-badge`}
                              className="inline-flex items-center gap-1 shrink-0 mt-0.5 text-[9px] font-black uppercase tracking-[0.2em] text-red-400 border border-red-500/50 rounded px-1.5 py-0.5"
                            >
                              <TrendingUp className="h-3 w-3" /> Top
                            </span>
                          ) : (
                            <ArrowUpRight className="h-3.5 w-3.5 text-gray-600 shrink-0 mt-0.5 transition-all duration-200 group-hover:text-red-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 leading-snug">{description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Tag>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
